import {
    Box,
    Text,
    Flex,
    HStack,
    Icon,
    Badge,
} from '@chakra-ui/react';
import React, { useMemo, useState } from 'react';
import { TbAlertTriangle, TbCheck, TbFileDescription } from 'react-icons/tb';
import EMSDetailModal from '../../modals/EMSDetailModal';
import Timeline, { TimelineGroupData } from '../Timeline';
import type { OrderEmsPush } from '../../hooks/useGetOrderDetail';

export type EmsExportPush = OrderEmsPush;

interface EmsExportHistoryTimelineProps {
    pushes: Array<EmsExportPush>;
}

const EMS_NODE_COLOR = '#3182CE';
const EMS_ICON_BG = '#E7F0FC';

function formatDateGroup(dateStr: string): string {
    const date = new Date(dateStr);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const pushDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    if (pushDate.getTime() === today.getTime()) return 'Today';

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (pushDate.getTime() === yesterday.getTime()) return 'Yesterday';

    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function formatTime(dateStr: string): string {
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const time = d.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });
    return `${month}/${day} • ${time}`;
}

function groupByDate(pushes: EmsExportPush[]): Map<string, EmsExportPush[]> {
    const groups = new Map<string, EmsExportPush[]>();
    for (const push of pushes) {
        const key = formatDateGroup(push.timestamp);
        const group = groups.get(key);
        if (group) {
            group.push(push);
        } else {
            groups.set(key, [push]);
        }
    }
    return groups;
}

const EmsExportHistoryTimeline = ({ pushes }: EmsExportHistoryTimelineProps) => {
    const [selectedEMSId, setSelectedEMSId] = useState<string | null>(null);
    const [isEMSModalOpen, setIsEMSModalOpen] = useState(false);

    const openEmsModal = (emsId: string) => {
        setSelectedEMSId(emsId);
        setIsEMSModalOpen(true);
    };

    const exportNumbers = useMemo(() => {
        const map = new Map<string, number>();
        pushes.forEach((push, index) => {
            map.set(push._id, pushes.length - index);
        });
        return map;
    }, [pushes]);

    const timelineGroups: TimelineGroupData[] = useMemo(() => {
        const grouped = groupByDate(pushes);
        return Array.from(grouped.entries()).map(([dateLabel, groupPushes]) => ({
            key: dateLabel,
            label: dateLabel,
            rightLabel: `${groupPushes.length} export${groupPushes.length === 1 ? '' : 's'}`,
            items: groupPushes.map((push) => {
                const exportNumber = exportNumbers.get(push._id) ?? 0;
                return {
                    key: push._id,
                    nodeColor: EMS_NODE_COLOR,
                    content: (
                        <Box
                            as="button"
                            type="button"
                            textAlign="left"
                            w="100%"
                            bg="white"
                            py={3}
                            borderBottom="1px solid"
                            borderColor="gray.100"
                            cursor="pointer"
                            _hover={{ bg: 'gray.50' }}
                            onClick={() => openEmsModal(push._id)}
                        >
                            <Flex align="center" gap={3}>
                                <Flex
                                    w="32px"
                                    h="32px"
                                    borderRadius="full"
                                    bg={EMS_ICON_BG}
                                    align="center"
                                    justify="center"
                                    flexShrink={0}
                                >
                                    <Icon
                                        as={TbFileDescription as React.ElementType}
                                        w={4}
                                        h={4}
                                        color={EMS_NODE_COLOR}
                                    />
                                </Flex>
                                <Box flex={1} minW={0}>
                                    <Flex align="center" justify="space-between" gap={2} minW={0}>
                                        <HStack spacing={1.5} minW={0} flex={1}>
                                            <Text
                                                fontSize="sm"
                                                fontWeight="semibold"
                                                color="gray.800"
                                                flexShrink={0}
                                                lineHeight="1.3"
                                            >
                                                EMS Export {exportNumber}
                                            </Text>
                                            <Badge
                                                display="inline-flex"
                                                alignItems="center"
                                                gap={0.5}
                                                fontSize="9px"
                                                px={1.5}
                                                py={0.5}
                                                borderRadius="md"
                                                flexShrink={0}
                                                textTransform="none"
                                                fontWeight="semibold"
                                                colorScheme={push.wasSubmitted ? 'green' : 'orange'}
                                                variant="subtle"
                                            >
                                                <Icon
                                                    as={(push.wasSubmitted ? TbCheck : TbAlertTriangle) as React.ElementType}
                                                    boxSize={3}
                                                />
                                                {push.wasSubmitted ? 'Submitted' : 'Not submitted'}
                                            </Badge>
                                        </HStack>
                                        <Text fontSize="xs" color="gray.400" flexShrink={0} whiteSpace="nowrap">
                                            {formatTime(push.timestamp)}
                                        </Text>
                                    </Flex>
                                </Box>
                            </Flex>
                        </Box>
                    ),
                };
            }),
        }));
    }, [pushes, exportNumbers]);

    if (pushes.length === 0) {
        return (
            <Text fontSize="sm" color="gray.500" textAlign="center" py={8}>
                No EMS exports yet
            </Text>
        );
    }

    return (
        <>
            <Timeline groups={timelineGroups} />
            {selectedEMSId && (
                <EMSDetailModal
                    isOpen={isEMSModalOpen}
                    onClose={() => setIsEMSModalOpen(false)}
                    emsId={selectedEMSId}
                />
            )}
        </>
    );
};

export default EmsExportHistoryTimeline;
